import { ForwardedRef, MutableRefObject, forwardRef, useEffect, useRef, useState } from 'react';

import { IconCheckFilled, IconErrorFilled } from 'components/Icons';
import {
  EndIcon,
  InformationArea,
  MultilineContainer,
  StartIcon,
  TextArea,
} from 'components/Input/InputField/BaseInputField/styles';
import { BaseInputFieldProps } from 'components/Input/InputField/BaseInputField/types';
import Typography from 'components/Typography';
import { QUESTION_INPUT_FIELD_ID } from 'constants/id';
import { useField } from 'formik';
import { themeVariables } from 'theme/globalStyles';
import { sleep } from 'utils/async';
import ErrorLabel from './ErrorLabel';

const BaseTextAreaField = (props: BaseInputFieldProps, parentRef: ForwardedRef<HTMLElement>) => {
  const { startIcon, endIcon, showValid = true, ...fieldProps } = props;

  // Removing `required` from props so Input field does not start out in an error state
  const { required, ...textAreaProps } = props;

  const [field, meta] = useField(fieldProps);

  const internalRef = useRef<HTMLInputElement>(null);
  const elemRef = (parentRef as MutableRefObject<HTMLElement>) || internalRef; // parent ref is to support emoji selector for question input.

  const [currEndIcon, setCurrEndIcon] = useState(endIcon);

  // Watch for changes on error
  useEffect(() => {
    if (meta.touched && meta.error) {
      if (elemRef.current instanceof HTMLInputElement)
        elemRef.current?.setCustomValidity(meta.error);

      setCurrEndIcon(<IconErrorFilled />);
      return;
    }
    if (elemRef.current instanceof HTMLInputElement) elemRef.current?.setCustomValidity('');

    setCurrEndIcon(showValid && field.value ? <IconCheckFilled /> : endIcon);
  }, [meta.touched, meta.error, endIcon, showValid, field.value]);

  const autoResizeTextArea = async () => {
    await sleep(500); // wait for handlers and hooks to sync. Must be at top of function. May be unpredictable without. Esp on submission.

    const { spacing } = themeVariables;
    const isQuestionInputField = props.id === QUESTION_INPUT_FIELD_ID;

    // re-initialize to default height as user deletes (all) field input
    const DEFAULT_HEIGHT = isQuestionInputField ? spacing['5Xl'] : spacing['8Xl'];
    elemRef.current?.setAttribute('style', `height: ${DEFAULT_HEIGHT}; overflow-y: hidden;`);

    // height of input will adjust dynamically again
    elemRef.current?.setAttribute('style', `height: ${elemRef.current.scrollHeight}px;`);
  };

  return (
    <MultilineContainer id='base-textarea-container'>
      {startIcon && <StartIcon multiline>{startIcon}</StartIcon>}
      <TextArea
        onInput={autoResizeTextArea}
        ref={elemRef}
        {...field}
        {...meta}
        {...textAreaProps}
        endIcon={!!currEndIcon}
        onBlur={field.onBlur}
      />
      <EndIcon multiline>{currEndIcon}</EndIcon>
      {meta.error && (
        <InformationArea id='input-field-information'>
          <ErrorLabel name={field.name} />
        </InformationArea>
      )}
      {!meta.error && required && (
        <InformationArea id='input-field-information'>
          <Typography variant='body' size='xs'>
            Required
          </Typography>
        </InformationArea>
      )}
    </MultilineContainer>
  );
};

export default forwardRef(BaseTextAreaField);
